import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Oval } from "react-loader-spinner";
import { FACET_AUTOGENERATE_SELECT_SOURCE_MSG } from "../../tars-constants";
import { filterFacetAutogenerateKeys } from "./facet-autogenerate-key-filter";

interface FacetAutogenerateKeyListProps {
  keys: string[];
  loading: boolean;
  fetchError: string | null;
  needsSourceSelection: boolean;
  selected: Set<string>;
  onToggle: (key: string) => void;
  onToggleAll: () => void;
  onToggleFiltered: (filteredKeys: string[]) => void;
}

export default function FacetAutogenerateKeyList({
  keys,
  loading,
  fetchError,
  needsSourceSelection,
  selected,
  onToggle,
  onToggleAll,
  onToggleFiltered,
}: FacetAutogenerateKeyListProps) {
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    setSearchQuery("");
  }, [keys]);

  const filteredKeys = useMemo(
    () => filterFacetAutogenerateKeys(keys, searchQuery),
    [keys, searchQuery]
  );
  const isFiltering = searchQuery.trim().length > 0;
  const showKeyControls = keys.length > 0 && !loading && !fetchError && !needsSourceSelection;
  const visibleSelectedCount = filteredKeys.filter((key) => selected.has(key)).length;
  const allVisibleSelected =
    filteredKeys.length > 0 && visibleSelectedCount === filteredKeys.length;

  const handleToggleAll = () => {
    if (isFiltering) onToggleFiltered(filteredKeys);
    else onToggleAll();
  };

  return (
    <div className="mt-3 rounded-md border border-gray-200">
      {showKeyControls && (
        <div className="border-b border-gray-200 p-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              strokeWidth={2}
              aria-hidden
            />
            <input
              type="search"
              placeholder="Search keys"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-2 text-sm text-gray-800 outline-none placeholder:text-gray-400 focus:border-indigo-400"
              aria-label="Search source field keys"
            />
          </div>
        </div>
      )}
      {showKeyControls && (
        <label className="flex cursor-pointer items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={allVisibleSelected}
            onChange={handleToggleAll}
          />
          <span className="font-medium">
            {isFiltering ? `Select all (${filteredKeys.length})` : "Select all"}
          </span>
        </label>
      )}
      <ul className="max-h-52 space-y-0.5 overflow-y-auto p-2" aria-label="Source field keys">
        {needsSourceSelection && (
          <li className="px-1 py-3 text-sm text-amber-800">{FACET_AUTOGENERATE_SELECT_SOURCE_MSG}</li>
        )}
        {!needsSourceSelection && loading && (
          <li className="flex items-center justify-center gap-2 py-4 text-sm text-gray-500">
            <Oval height={16} width={16} color="#6b7280" ariaLabel="Loading" visible />
            Loading keys…
          </li>
        )}
        {!needsSourceSelection && !loading && fetchError && (
          <li className="px-1 py-2 text-sm leading-snug text-amber-800">{fetchError}</li>
        )}
        {!needsSourceSelection && !loading && !fetchError && keys.length === 0 && (
          <li className="px-1 py-2 text-sm text-gray-500">
            No qr_, profile_, or piv_ fields found in sample document.
          </li>
        )}
        {!needsSourceSelection &&
          !loading &&
          !fetchError &&
          keys.length > 0 &&
          isFiltering &&
          filteredKeys.length === 0 && (
            <li className="px-1 py-2 text-sm text-gray-500">No keys match your search.</li>
          )}
        {!needsSourceSelection &&
          !loading &&
          !fetchError &&
          filteredKeys.map((key) => (
            <li key={key}>
              <label className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 hover:bg-gray-50">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={selected.has(key)}
                  onChange={() => onToggle(key)}
                />
                <span className="min-w-0 break-all font-mono text-xs text-gray-800">{key}</span>
              </label>
            </li>
          ))}
      </ul>
    </div>
  );
}
